// Closed set of "Begin with a question" starters surfaced on the dashboard.
// Each starter declares exactly how it should pre-tune the New Review form.
// The question itself becomes a `focus_hint` that the prompt builder injects
// — it is NOT used as the assignment brief.

export type StarterId =
  | "structural"
  | "bibliography"
  | "integrity"
  | "weakest_argument"
  | "source_use"
  | "pre_submit_questions";

export type StarterPreset = {
  id: StarterId;
  label: string;
  reviewMode: "quick_integrity" | "structural" | "full_critique";
  audience: "student" | "tutor" | "both";
  checkBibliography: boolean;
  focusHint: string;
};

export const STARTERS: StarterPreset[] = [
  {
    id: "structural",
    label: "Review my essay for structural coherence",
    reviewMode: "structural",
    audience: "student",
    checkBibliography: false,
    focusHint:
      "Focus on structural coherence: argument arc, paragraph sequence, signposting, and intro/conclusion alignment.",
  },
  {
    id: "bibliography",
    label: "Audit my bibliography against my in-text citations",
    reviewMode: "quick_integrity",
    audience: "student",
    checkBibliography: true,
    focusHint:
      "Prioritise a bibliography audit: every in-text citation must resolve to a reference and vice versa; flag incomplete, inconsistent or implausible entries.",
  },
  {
    id: "integrity",
    label: "Flag any academic integrity risks in my draft",
    reviewMode: "quick_integrity",
    audience: "student",
    checkBibliography: true,
    focusHint:
      "Prioritise integrity risks: unsupported claims, possible fabrication, citation gaps, AI-disclosure risks, paraphrase-without-attribution.",
  },
  {
    id: "weakest_argument",
    label: "Where is my argument weakest?",
    reviewMode: "full_critique",
    audience: "student",
    checkBibliography: false,
    focusHint:
      "Identify the weakest argument(s): claims with thin evidence, missing counterargument, logical gaps, or over-reach.",
  },
  {
    id: "source_use",
    label: "Are my sources used critically or just descriptively?",
    reviewMode: "full_critique",
    audience: "student",
    checkBibliography: true,
    focusHint:
      "Evaluate source use: distinguish critical engagement (weighing, comparing, challenging) from descriptive summary; flag passages that only describe.",
  },
  {
    id: "pre_submit_questions",
    label: "What questions should I ask before I submit?",
    reviewMode: "full_critique",
    audience: "student",
    checkBibliography: true,
    focusHint:
      "Emphasise the Socratic 'questions to consider before submitting' section: marker-style probes the student should answer before pressing submit.",
  },
];

export const STARTER_IDS = STARTERS.map((s) => s.id) as [StarterId, ...StarterId[]];

export const STARTER_BY_ID: Record<StarterId, StarterPreset> = Object.fromEntries(
  STARTERS.map((s) => [s.id, s]),
) as Record<StarterId, StarterPreset>;
